import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface TimezoneContextType {
  timezone: string;
  offset: number;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [offset, setOffset] = useState<number>(new Date().getTimezoneOffset());
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const detectAndUpdateTimezone = async () => {
      try {
        // Get the user's timezone using the Intl API
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const detectedOffset = new Date().getTimezoneOffset();

        // If user is authenticated, send timezone info to backend
        if (isAuthenticated) {
          await api.post('/api/user/timezone/', {
            timezone: detectedTimezone,
            offset: detectedOffset
          });
        }

        setTimezone(detectedTimezone);
        setOffset(detectedOffset);
      } catch (error) {
        console.error('Error updating timezone:', error);
      }
    };

    detectAndUpdateTimezone();
  }, [isAuthenticated]);

  return (
    <TimezoneContext.Provider value={{ timezone, offset }}>
      {children}
    </TimezoneContext.Provider>
  );
};
